import { useId } from 'react';
import { useSelector } from 'react-redux';
import { selectors } from 'darwin_core';
import { IDEAL_HEATING_SUPPORT_NUMBER } from 'darwin_core/utils/consts.js';

// The device-offline help — a port of the app's OfflineHelp
// (app/src/components/OfflineHelp/index.js), shown when a zone cannot be reached.
// It does not just say "offline": it names WHICH link is down and gives the steps
// that fix that link.
//
// The two causes must never be confused, because they send the user to different
// hardware. An interface that has lost the CLOUD is a Wi-Fi problem at the router;
// an interface that has lost the THERMOSTAT is a zigbee/battery problem at the
// thermostat. So this branches on the two connection flags SEPARATELY and never on
// darwin_core's isZoneOnline, which is `prt && si` and has already thrown away
// which of the two is down.
//
// Cloud first when both are down, exactly as the app orders it. A zone's
// connectionStatus arrives in the same homes frame as si.connected
// (device-reducer.js), and getPrtConnectionStatus reads a missing field as false,
// so while the interface is off the cloud that flag is stale-or-absent rather than
// evidence about the thermostat. Leading with batteries there would send the user
// to the wrong device for what is a router outage.
//
// Copy is VERBATIM from the app, including its plain-text phone number: only the
// no-cloud-licence screen makes the number dialable (see NonConnectedScreen), and
// that is deliberate — an offline interface is a self-fix, not a support call.
// The steps are a real ordered list here, so the browser numbers them; the app's
// manual "1." prefixes exist only because React Native has no <ol>.
export function OfflineHelp({ siConnected, prtConnected, isHpi = false }) {
  const titleId = useId();
  const heatSource = isHpi ? 'heat pump' : 'boiler';

  if (!siConnected) {
    return (
      <section className="offline-help" aria-labelledby={titleId}>
        <h2 id={titleId} className="offline-help__title">
          Your interface has lost connection with the Cloud
        </h2>
        <p className="offline-help__intro">
          Try these steps to resolve the issue:
        </p>
        <ol className="offline-help__steps">
          <li>Check your Wi-Fi router is on and operating</li>
          <li>
            Check other Wi-Fi devices can connect to the internet, such as smart
            home assistants
          </li>
          <li>
            Check your Wi-Fi router or {heatSource} position has not been moved
          </li>
        </ol>
        <p className="offline-help__footer">
          If the problem still persists contact Ideal Heating on{' '}
          {IDEAL_HEATING_SUPPORT_NUMBER}
        </p>
      </section>
    );
  }

  if (!prtConnected) {
    return (
      <section className="offline-help" aria-labelledby={titleId}>
        <h2 id={titleId} className="offline-help__title">
          Your interface has lost connection with your thermostat
        </h2>
        <p className="offline-help__intro">
          Try these steps to resolve the issue:
        </p>
        <ol className="offline-help__steps">
          <li>Check the batteries in your thermostat</li>
          <li>
            Check the Power and Zigbee LED is solid green on the interface
          </li>
          <li>Move the thermostat closer to the interface if possible</li>
        </ol>
        <p className="offline-help__footer">
          If the problem still persists contact Ideal Heating on{' '}
          {IDEAL_HEATING_SUPPORT_NUMBER}
        </p>
      </section>
    );
  }

  return null;
}

// The store reads behind the block, in one place so all four zone surfaces agree
// on both the flags and the wording. `show` is what a screen swaps its controls
// on; the rest spreads straight into <OfflineHelp/>.
//
// Deliberately NOT folded in here: our own WebSocket state. These flags are the
// cloud's last word on the device, which a dropped browser socket does not change
// — it only stops them being refreshed. The app can conflate the two because a
// socket failure there raises a full-screen overlay (screens/Overlay/Loading.js)
// that hides the zone entirely; on the web the ConnectionBanner above every screen
// speaks for our connection, and this speaks for the device's.
export function useZoneOfflineHelp(homeId, zoneId) {
  const siConnected = useSelector((state) =>
    Boolean(selectors.getInterfaceConnectionStatus(state, homeId)),
  );
  const prtConnected = useSelector((state) =>
    Boolean(selectors.getPrtConnectionStatus(state, homeId, zoneId)),
  );
  const isDhw = useSelector((state) => selectors.isDhw(state, homeId, zoneId));
  const isHpiZone = useSelector((state) =>
    selectors.isHpi(state, homeId, zoneId),
  );

  // The heat-source word in the Wi-Fi steps. Both of the app's hot-water screens
  // hard-code isHpi={false} (HaloHotWaterZone / HaloAirHotWaterZone), so even a
  // Halo Air cylinder reads "boiler" there; mirrored rather than corrected, so the
  // two clients say the same thing.
  return {
    show: !siConnected || !prtConnected,
    siConnected,
    prtConnected,
    isHpi: isDhw ? false : isHpiZone,
  };
}
